import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { UrlDetailComponent } from '../url-detail/url-detail';
import type {
  ImpactBriefEmptyState,
  ImpactBriefHeadline,
  ImpactPackage,
  ImpactPackageLink,
  ImpactPackagePage,
  ImpactBriefMode,
  ImpactBriefReadiness,
  ImpactBriefTotals
} from '../../services/impact-brief.service';
import {
  impactBriefEmptyMessage,
  impactBriefHeadlineStats,
  impactPackageHeading,
  impactPageChangeStats,
  impactPackageMoreLinks,
  impactPackageMorePages,
  impactChangeColumnLabel,
  impactPageChangeCell,
  impactPageChangeTone,
  impactStripHeading,
  impactLinkRowSummary,
  impactLinkPages,
  impactLinkMoreLabel,
  type ImpactLinkPage,
  impactBriefSummary,
  impactPackagePageRows,
  impactPackagePagePath,
  impactPackageProgress,
  impactPackageStats,
  type ImpactPackageHeading,
  type ImpactPackageStat
} from '../../utils/impact-package-display';
import { uncapturedGapNote } from '../../utils/uncaptured-gap';

/**
 * This period's impact packages — the week plan (IMPACT_BRIEF_PLAN.md).
 *
 * One component for both surfaces: Dashboard (IB2) shows it above the movers,
 * Issues (IB3) shows it above the full living-brief queue. The plan's trust bar
 * is that the two show the *same* package ids, titles and counts for the same
 * domain and period, so this renders IB1's payload verbatim — no re-sorting, no
 * re-capping, no locally composed titles — and the surfaces differ only through
 * the presentation inputs below.
 *
 * Packages arrive already ranked and already capped. `pages` is the capped list;
 * `pageCount` is the real size, and that is what an operator sizes a week on.
 */
@Component({
  selector: 'app-impact-packages',
  standalone: true,
  imports: [CommonModule, RouterModule, UrlDetailComponent],
  templateUrl: './impact-packages.html',
  styleUrl: './impact-packages.css'
})
export class ImpactPackagesComponent {
  constructor(private readonly router: Router) {}

  /** Already ranked and capped by the composer. Rendered in order. */
  @Input({ required: true }) packages: ImpactPackage[] = [];

  /**
   * The content packages `/review` walks in place of the link packages among the slots
   * (`reviewFillPackages`, ISSUES_REVIEW_FINDINGS §39). Listed below the slots under "Also on
   * /review", so every page on the belt has a line on this strip (§0ck). Not slots: nothing in the
   * header counts them except the Review button, which is the belt's size.
   */
  @Input() reviewFillPackages: ImpactPackage[] = [];

  /**
   * Rendered above the packages when provided. Optional because Issues already
   * carries its own executive summary and a second one would compete with it.
   */
  @Input() headline: ImpactBriefHeadline | null = null;

  /**
   * Whether the data behind this brief has finished arriving (§0ax).
   *
   * Rendered as one line and nothing more. It does **not** filter packages, hide
   * the belt or change a single number on this strip: an operator reading the
   * dashboard at 07:50 and again at 09:50 used to see two different weeks of
   * work with no way to tell which was the real one, and telling them fixes
   * that. Hiding the dashboard until the pipeline finishes is the stronger
   * answer and it inherits every stall in the pipeline, so it waits until a
   * cycle can reliably finish itself (§0aw).
   */
  @Input() readiness: ImpactBriefReadiness | null = null;

  /**
   * The strip's own title. Left unset it follows `mode` — see
   * `impactStripHeading`, and `headingText` below.
   */
  @Input() heading: string | null = null;

  @Input() subhead: string | null = 'The biggest things to work on, grouped into packages.';

  /** Issues mounts this under its own section chrome and turns the header off. */
  @Input() showHeader = true;

  /**
   * Why the composer returned no packages, so the empty state can say which
   * empty it is. Passed straight through from `payload.emptyState`.
   */
  @Input() emptyState: ImpactBriefEmptyState | null = null;

  /**
   * Whether this brief had a crawl pair to measure against — straight through
   * from `payload.mode`.
   *
   * The last column is the only thing that reads it. On a first crawl every
   * row's `periodChange` is null by construction, so CHANGE renders a dash on
   * every row of the strip while the gap figure that separates a page worth
   * 9,059 visits from one worth 34 sits unrendered on the same row.
   *
   * Null is treated as monitoring, so a host that has not been taught to pass
   * it keeps the column it has always had rather than silently relabelling it.
   */
  @Input() mode: ImpactBriefMode | null = null;

  /**
   * `payload.totals`, for the line under the strip's title (§4 B5).
   *
   * The strip used to summarise itself as "5 packages · 122 pages" while the
   * button beside it offered "Review 49 pages". Both numbers were right —
   * `pageCount` is a sum across packages and the belt is a set, and two link
   * packages contributed 73 source pages the belt never opens — but an operator
   * reads them as one claim and one of them is disprovable by pressing the
   * other. Adding packages only widened it.
   *
   * On a first crawl the line now counts the belt and names what was held back.
   * Optional, because a host that does not pass it keeps the old summary rather
   * than rendering a blank.
   */
  @Input() totals: ImpactBriefTotals | null = null;

  /**
   * Overrides the copy `emptyState` would pick. For a surface whose own context
   * makes a different sentence truer — a first-run home, say. Left unset, the
   * shared wording applies, which is what keeps Dashboard and Issues aligned.
   */
  @Input() emptyMessage: string | null = null;

  /**
   * The "Open in Issues" escape hatch, in the sheet's footer. On by default,
   * because from the Dashboard it is the whole drill-down.
   *
   * Issues turns it off: there it points at the page the operator is already on,
   * and a link that navigates nowhere reads as a broken one. The full queue is
   * already directly below the strip on that surface, which is the same
   * destination by other means.
   *
   * One link, not one per package. Four packages used to render four identical
   * links to one destination, which is three more than the screen needs and
   * four more primary actions than the rows are allowed.
   */
  @Input() showOpenInIssues = true;

  /**
   * "Review 45 pages →" in the strip's header — the way into the belt.
   *
   * On by default, and on for both surfaces: the queue is the same queue from
   * either one, and a review loop that exists on the Dashboard but not in
   * Issues would be a second answer to "how do I work through this?".
   */
  @Input() showReview = true;

  /**
   * The domain the packages were composed against, passed down to the embedded
   * URL detail so an expanded page reads its diagnoses in the same scope. The
   * parent already knows it — Dashboard from the movers' `scopeDomain`, Issues
   * from the domain picker — and passing it beats having this component guess,
   * which in a multi-domain workspace is how a peek ends up describing a
   * different site than the card above it.
   */
  @Input() domain: string | null = null;

  /**
   * Drop rows for pages already dealt with this period.
   *
   * On for the Dashboard strip and off everywhere else, which is the whole
   * point of it being an input. The strip gives a package five row slots and
   * exists to answer "what is next"; a row you already sent is not next. The
   * Inventory keeps its rows, because that is the surface you go to when you
   * want to find a page — including one you have already handled.
   *
   * The count above the rows does not move with them: the package header keeps
   * "N of M done", so hiding a row never reads as work that disappeared.
   */
  @Input() hideDecidedPages = false;

  /**
   * The package whose full page list is being fetched, if any.
   *
   * Owned by the host because the host owns the fetch. Passed back down so the
   * "+13 more" row can say it is working rather than looking inert for the
   * second or two the synthesis takes.
   */
  @Input() expandingPackageId: string | null = null;

  /**
   * "+13 more pages in this package" was clicked.
   *
   * An output rather than a fetch here: this component renders a payload and
   * derives nothing, which is the trust bar the whole strip is built on. Both
   * hosts already own a brief and know how to reload one.
   */
  @Output() expandPackage = new EventEmitter<string>();

  /**
   * The open page row, if any, keyed by package as well as url: the same page
   * can legitimately sit in two packages (a decay prefix and a cluster, say),
   * and clicking it in one must not silently open it in the other.
   *
   * At most one at a time, like the Dashboard movers.
   */
  expandedPageKey: string | null = null;



  /**
   * What the brief admits about its own data, or null when there is nothing
   * to disclose. Not always a "still building" line: on a finished domain it
   * says the counts are final, which is the whole point of composing it
   * server-side where `building` is known.
   *
   * Read off `summary` rather than assembled here: two surfaces render this
   * component, and a sentence composed in the UI is a sentence the other surface
   * can word differently.
   */
  readinessNote(): string | null {
    if (!this.readiness || this.readiness.complete) return null;
    return this.readiness.summary;
  }

  /** The empty-state sentence actually rendered — override, else by state. */
  get emptyText(): string {
    return this.emptyMessage ?? impactBriefEmptyMessage(this.emptyState);
  }

  /** The title actually rendered — override, else by mode. */
  headingText(): string {
    return this.heading ?? impactStripHeading(this.mode);
  }

  /**
   * Link packages whose rows are showing.
   *
   * Collapsed by default (§4 B6) and local to this component, because nothing
   * has to be fetched: the links are already in the payload, capped at
   * `caps.maxLinksPerPackage`. Contrast `expandPackage`, which asks the host
   * for pages the server did not send.
   */
  private expandedLinkPackageIds = new Set<string>();

  isLinksExpanded(pkg: ImpactPackage): boolean {
    return this.expandedLinkPackageIds.has(pkg.id);
  }

  toggleLinks(pkg: ImpactPackage, event?: Event): void {
    event?.preventDefault();
    if (this.expandedLinkPackageIds.has(pkg.id)) {
      this.expandedLinkPackageIds.delete(pkg.id);
    } else {
      this.expandedLinkPackageIds.add(pkg.id);
    }
  }

  onLinksKeydown(pkg: ImpactPackage, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    this.toggleLinks(pkg, event);
  }

  /** "15 links ready to paste" — the row the package collapses to. */
  linkRowSummary(pkg: ImpactPackage): string {
    return impactLinkRowSummary(pkg);
  }

  /** The sections the table renders: the slots, then the packages `/review` fills from (§39). */
  sections(): ImpactPackage[] {
    return this.reviewFillPackages.length ? [...this.packages, ...this.reviewFillPackages] : this.packages;
  }

  /** On `/review` in place of a link package's slot, not a slot of its own (§39). */
  isReviewFill(pkg: ImpactPackage): boolean {
    return this.reviewFillPackages.some((fill) => fill.id === pkg.id);
  }

  trackByPackage(_index: number, pkg: ImpactPackage): string {
    return pkg.id;
  }

  trackByPage(_index: number, page: ImpactPackagePage): string {
    return page.urlId;
  }

  /** The expanded link package, one entry per source page (§0az). */
  linkPages(pkg: ImpactPackage): ImpactLinkPage[] {
    return impactLinkPages(pkg);
  }

  trackByLinkPage(_index: number, page: ImpactLinkPage): string {
    return page.sourceUrlId;
  }

  trackByLink(_index: number, link: ImpactPackageLink): string {
    return link.suggestionKey;
  }

  /** The header stats for one package: pages / decaying / lost / gap. */
  stats(pkg: ImpactPackage): ImpactPackageStat[] {
    return impactPackageStats(pkg, this.headline?.gapScale ?? null);
  }

  /** The same two amounts for the whole brief, in the sheet's own header. */
  headlineStats(): ImpactPackageStat[] {
    return impactBriefHeadlineStats(this.headline);
  }

  /** What moved on this page, for the line above its expanded panel. */
  pageWhy(page: ImpactPackagePage): ImpactPackageStat[] {
    return impactPageChangeStats(page, this.headline?.gapScale ?? null);
  }

  /**
   * What "uncaptured" is, said once under the strip's numbers: modelled clicks converted by this
   * site's Search Console ratio, or demand on our model (owner D4). Only when there is a gap shown.
   */
  gapNote(): string | null {
    const gap = this.headline?.gapSum;
    if (gap === null || gap === undefined || !Number.isFinite(gap)) return null;
    return uncapturedGapNote(this.headline?.gapScale ?? null);
  }

  /**
   * Nothing left to review in this package.
   *
   * A quiet state, not a celebratory one: the section keeps its place and its
   * numbers and simply steps back in ink. The strip is a map of the period, and
   * a package that vanished or jumped to the bottom when you finished it would
   * take the record of what the week held with it — and re-rank the three
   * packages you had not finished, under your cursor.
   *
   * Read off the server's `completed` rather than compared here. The dashboard
   * is sent five pages of an eighteen-page package; a local comparison would
   * call it finished after five decisions.
   */
  isCompleted(pkg: ImpactPackage): boolean {
    return impactPackageProgress(pkg).completed;
  }

  /** IB1's title split, plus which kind of scope it names. */
  packageHeading(pkg: ImpactPackage): ImpactPackageHeading {
    return impactPackageHeading(pkg);
  }

  /**
   * "4 packages · 45 pages · 25 lost ground" — what the strip covers, in the
   * line under its title.
   *
   * `packages.length` is what is on screen; `pageCount` and `decayingPageCount`
   * come off `headline`, which is IB1's count across the whole brief. They are
   * different numbers on purpose and the wording says which is which.
   */
  briefSummary(): string {
    return impactBriefSummary(this.packages.length, this.headline, this.mode, this.totals);
  }

  /** The page rows to render, decided ones dropped where the host asks. */
  pageRows(pkg: ImpactPackage): ImpactPackagePage[] {
    return impactPackagePageRows(pkg, this.hideDecidedPages).rows;
  }

  /**
   * Paired with `pageRows` deliberately: when rows are hidden the "+N more" is
   * recounted against them, so the list and its footer can never disagree.
   */
  morePages(pkg: ImpactPackage): number {
    return impactPackagePageRows(pkg, this.hideDecidedPages).more;
  }

  moreLinks(pkg: ImpactPackage): number {
    return impactPackageMoreLinks(pkg);
  }

  /**
   * "+13 more pages in this package", or the link package's equivalent, or ''.
   *
   * One accessor rather than two `*ngIf`s in the footer row: a link package
   * must never claim "+50 more pages" off a `pageCount` that counts its source
   * pages, and branching once here is how that stays impossible.
   */
  /** Whether "+N more" is a control on this package, or just a count. */
  canExpand(pkg: ImpactPackage): boolean {
    return !this.isLinkPackage(pkg) && this.morePages(pkg) > 0;
  }

  isExpanding(pkg: ImpactPackage): boolean {
    return this.expandingPackageId === pkg.id;
  }

  onExpandPackage(pkg: ImpactPackage, event?: Event): void {
    event?.stopPropagation();
    if (!this.canExpand(pkg) || this.isExpanding(pkg)) return;
    this.expandPackage.emit(pkg.id);
  }

  moreLabel(pkg: ImpactPackage): string {
    if (this.isLinkPackage(pkg)) return impactLinkMoreLabel(pkg);
    const n = this.morePages(pkg);
    return n > 0 ? `+${n} more ${n === 1 ? 'page' : 'pages'} in this package` : '';
  }

  pagePath(page: ImpactPackagePage): string {
    return impactPackagePagePath(page);
  }

  /**
   * The Keyword column.
   *
   * `keyword` is precomputed by IB1 — the page's resolved SERP target query,
   * else its demand claim. This column used to render `clusterLabel`, which is
   * a *grouping* of keywords and not a keyword: eighteen rows of "Project
   * management & delivery" under a package header already titled "Project
   * management & delivery", and an em dash on every page that happens to be
   * unclustered. The cluster is the package's scope and is stated once, where
   * scopes belong.
   *
   * An em dash when the page has neither — blank cells in a dense table read as
   * a rendering failure.
   */
  pageKeyword(page: ImpactPackagePage): string {
    return (page.keyword || '').trim() || '—';
  }

  /**
   * The hover title on that cell, which is where the source distinction lives.
   *
   * A fetched target query and an unfetched demand claim are different claims —
   * one has a measured position behind it, the other is where we would look if
   * we looked. Rendering them identically with no way to tell says the SERP ran
   * on a page where it never did. It is a `title` rather than a badge because
   * the difference matters when you are deciding whether to trust the row, not
   * while you are scanning twenty of them.
   */
  pageKeywordTitle(page: ImpactPackagePage): string | null {
    if (!(page.keyword || '').trim()) return null;
    return page.keywordSource === 'claim'
      ? `Demand claim — SERP not fetched for this page yet`
      : `Target query — the keyword this page's SERP position was measured against`;
  }

  /** Marks a claim-sourced keyword so it can read one step quieter. */
  isKeywordPending(page: ImpactPackagePage): boolean {
    return Boolean((page.keyword || '').trim()) && page.keywordSource === 'claim';
  }

  isLinkPackage(pkg: ImpactPackage): boolean {
    return pkg.kind === 'link';
  }

  /** A decaying page reads red, a recovering one green — same tone as movers. */
  changeTone(page: ImpactPackagePage): string {
    return impactPageChangeTone(page);
  }

  /** CHANGE, or OPPORTUNITY when there is no pair to change against. */
  changeColumnLabel(): string {
    return impactChangeColumnLabel(this.mode);
  }

  /** The movement, else the demand gap on a first crawl, else a dash. */
  changeCell(page: ImpactPackagePage): string {
    return impactPageChangeCell(page, this.mode);
  }



  pageKey(pkg: ImpactPackage, page: ImpactPackagePage): string {
    return `${pkg.id}::${page.urlId}`;
  }

  isPageExpanded(pkg: ImpactPackage, page: ImpactPackagePage): boolean {
    return this.expandedPageKey === this.pageKey(pkg, page);
  }


  /**
   * Open this page in review mode — the belt, at this URL.
   *
   * This used to open a drawer, and the drawer is gone. It was 80vw of fixed
   * panel over a 1100px centred column, which on a wide display covered the
   * table it opened from and overshot it on both sides; worse, its scrim made
   * the rows behind it dead, so reviewing a second page cost a close, a scroll
   * and a hunt for where you were. Review mode is that list walked properly —
   * see `components/review-mode`.
   *
   * The domain rides along so the belt is pinned to the same one the strip was
   * composed against, rather than letting the server resolve it a second time
   * and possibly differently in a multi-domain workspace.
   */
  openReview(page: ImpactPackagePage, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/review'], {
      queryParams: { url: page.url, ...(this.domain ? { domain: this.domain } : {}) }
    });
  }

  /**
   * How many pages the Review button promises.
   *
   * `reviewablePageCount`, not `pageCount`: the belt drops link packages and
   * shows a URL once, so on a brief with either the button was naming a number
   * the next screen contradicted — and it is the number a week gets sized on.
   *
   * Falls back to `pageCount` when the server has not sent the new field, which
   * is also the answer whenever the two agree.
   *
   * Not computed here. The dashboard receives packages capped at five pages
   * each while review mode asks for them uncapped, so counting what this
   * component was handed would promise twenty for a belt of forty-five.
   *
   * It now subtracts pages already decided this period as well, so it matches
   * the belt on a period halfway through and not only on a fresh one. That was
   * the last of `buildReviewQueue`'s three rules the server could not apply,
   * and it took the disposition set reaching the brief builder to close it.
   */
  reviewablePageCount(): number {
    return this.headline?.reviewablePageCount ?? this.headline?.pageCount ?? 0;
  }

  /** The strip's own way in: the whole belt, from the top. */
  reviewAll(event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/review'], {
      queryParams: this.domain ? { domain: this.domain } : {}
    });
  }



  togglePage(pkg: ImpactPackage, page: ImpactPackagePage, event?: Event): void {
    event?.preventDefault();
    const key = this.pageKey(pkg, page);
    this.expandedPageKey = this.expandedPageKey === key ? null : key;
  }

  /**
   * The row is the control, so it has to answer the keyboard the way a button
   * does: Enter and Space both toggle. Space is `preventDefault`ed inside
   * `togglePage` — without that it scrolls the page, because the row is a
   * `<tr tabindex="0">` and not a real `<button>`.
   *
   * It is not a `<button>`, and not `role="button"` either: that would take the
   * row out of the table's accessibility tree and with it every cell's link to
   * its column header, which on a five-column table is the information.
   * `aria-expanded` is a supported state of `role="row"`.
   */
  onRowKeydown(pkg: ImpactPackage, page: ImpactPackagePage, event: KeyboardEvent): void {
    if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
    this.togglePage(pkg, page, event);
  }
}
